import AntIcon from "@/components/icons/AntIcon";
import { List } from "@/components/list/List";
import { paginationStandardProps } from "@/utils/componentUtils";
import { getDatePickerDefaultRanges, getDatePickerFormat } from "@/utils/formUtils";
import { useWindowSize } from "@/utils/hooksUtils";
import { cssVariables, dateToIsoDateString } from "@/utils/utils";
import { Avatar, Button, DatePicker, Flex, Popover, Space, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetDashboardEvents, useGetPartnerConfigsAndSsoProperties } from "../../../../../common/api/queries";
import { Permission } from "../../../../../common/security/enums";
import { useHasPermissions } from "../../../../auth/hooks";
import { PartnerSsoType } from "../../../../partner/enums";
import { DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH } from "../../../utils";

export const DashboardEventsWidget = () => {
  const { t } = useTranslation();

  const windowSize = useWindowSize();

  const hasPartnerOthersPermission = useHasPermissions(Permission.PARTNER_OTHERS);

  const [dates, setDates] = useState<[Dayjs, Dayjs]>(() => [dayjs().startOf("month"), dayjs().endOf("month")]);

  const { data: partnerConfigs } = useGetPartnerConfigsAndSsoProperties(hasPartnerOthersPermission);

  const { data, isFetching } = useGetDashboardEvents({
    minDate: dateToIsoDateString(dates[0]),
    maxDate: dateToIsoDateString(dates[1]),
  });

  const vzdyViacLink = partnerConfigs?.configs.find((config) => config.ssoType === PartnerSsoType.VZDY_VIAC)?.link;

  return (
    <Flex vertical style={{ height: "100%" }}>
      <Flex justify="space-between">
        <h2 style={{ fontWeight: 600, fontSize: 28 }}>{t("dashboard.events.title")}</h2>

        <Space
          size={windowSize.innerWidth >= DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH ? "large" : "small"}
          style={{ marginBottom: 14 }}
        >
          <DatePicker.RangePicker
            style={{ borderRadius: 18 }}
            format={getDatePickerFormat()}
            presets={getDatePickerDefaultRanges({ hasNextMonth: true })}
            placeholder={[t("common.from"), t("common.to")]}
            value={dates}
            onChange={(value) => setDates(value as [Dayjs, Dayjs])}
            allowClear={false}
          />

          {vzdyViacLink ? (
            <Button href={vzdyViacLink} target="_blank" type="primary">
              VždyViac Portál
            </Button>
          ) : undefined}
        </Space>
      </Flex>

      <List
        loading={isFetching}
        gap={16}
        showEmpty
        pagination={{
          pageSize: 3,
          ...paginationStandardProps,
          hideOnSinglePage: true,
          showLessItems: true,
          size: windowSize.innerWidth >= DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH ? undefined : "small",
        }}
        style={
          !data?.events || data?.events.length === 0
            ? {
                backgroundColor: "white",
                borderRadius: 18,
                height: "100%",
                marginBottom: 60,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }
            : { height: "100%" }
        }
        items={data?.events ?? []}
        renderItem={(item) => (
          <Flex
            align="center"
            justify="space-between"
            style={{
              backgroundColor: "white",
              borderRadius: 18,
              border: "1px solid #f0f0f0",
              padding: "0 24px",
            }}
          >
            <Flex align="center" gap={16} style={{ flex: 1, maxWidth: "100%", height: 85 }}>
              <Tooltip title={t("dashboard.events.helpers.event")}>
                <Avatar
                  style={{ backgroundColor: cssVariables.colorGreen }}
                  size="large"
                  shape="square"
                  icon={<AntIcon type="schedule" />}
                />
              </Tooltip>

              <div style={{ flex: "1 0", width: 0 }}>
                <h4 style={{ margin: "0 0 var(--ant-margin-xxs)" }}>{item.title}</h4>

                <Flex style={{ color: "var(--ant-color-text-description)" }}>
                  {t("common.startAt")}: {dayjs(item.startAt).format("llll")}
                </Flex>
              </div>
            </Flex>

            <Popover
              title={<span style={{ paddingLeft: 16 }}>{t("dashboard.events.helpers.attendants")}</span>}
              content={
                <div style={{ width: 300, paddingTop: 8, borderTop: `1px solid ${cssVariables.colorHover}` }}>
                  <div style={{ height: 200, overflowY: "auto", paddingRight: 16 }}>
                    <List
                      items={item.attendants}
                      gap={0}
                      split
                      renderItem={(attendant) => (
                        <div style={{ padding: "8px 16px" }}>
                          <h4 style={{ margin: "0 0 var(--ant-margin-xxs)" }}>
                            {`${attendant.firstName} ${attendant.lastName} (${attendant.partnerIdNumber})`}
                          </h4>
                        </div>
                      )}
                    />
                  </div>

                  <div
                    className="margin-top-tiny"
                    style={{ borderTop: `1px solid ${cssVariables.colorHover}`, paddingTop: 8, paddingLeft: 16 }}
                  >
                    <Space size="small">
                      <span>{t("dashboard.events.helpers.freeCapacityCount")}:</span>
                      <b>{Math.max(0, item.capacity - item.attendants.length)}</b>
                    </Space>
                  </div>
                </div>
              }
            >
              <Button type="text">
                <span style={{ fontWeight: 500, fontSize: 16, marginRight: 6 }}>{item.attendants.length}</span>
                <AntIcon type="team" size="huge" />
              </Button>
            </Popover>
          </Flex>
        )}
      />
    </Flex>
  );
};
